import { Box } from "@material-ui/core";
import { useUser } from "@saleor/auth";
import Container from "@saleor/components/Container";
import PageHeader from "@saleor/components/PageHeader";
import { CUSTOMER_ORDER_BY } from "@saleor/config";
import SoldToPage from "@saleor/createOrder/components/SoldToPage";
import {
  contractDetailUrl,
  contractUrl,
  createOrderUrl,
  CreateOrderUrlQueryParams
} from "@saleor/createOrder/urls";
import CustomerBlockDialog from "@saleor/createOrderExport/components/CustomerBlockDialog";
import FullScreenButton from "@saleor/createOrderExport/components/FullScreenButton";
import { useContractsQuery, useGetCreditLimitQuery } from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useListSettings from "@saleor/hooks/useListSettings";
import useLocalPaginator, {
  useLocalPaginationState
} from "@saleor/hooks/useLocalPaginator";
import useNavigator from "@saleor/hooks/useNavigator";
import { makeStyles } from "@saleor/macaw-ui";
import { ListViews } from "@saleor/types";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { getSortParams, getSortUrlVariables } from "@saleor/utils/sort";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import SoldToList from "../components/SoldToList";
import { getSortQueryVariables } from "./sort";

interface ContractListProps {
  params: CreateOrderUrlQueryParams;
  id: string;
}

const useStyles = makeStyles(
  () => ({
    headerSoldToList: {
      display: "flex",
      justifyContent: "space-between"
    }
  }),
  { name: "SoldToList" }
);

const ContractList: React.FC<ContractListProps> = ({ id, params }) => {
  const [, dispatchAppState] = useAppState();
  // const paginate = usePaginator();
  const navigate = useNavigator();
  const { user } = useUser();
  const classes = useStyles();
  const { isStaff } = user;
  const [onOpenCustomerBlock, setOpenCustomerBlock] = useState<boolean>(false);
  const [filterVariables, setFilterVariables] = useState({
    companyIds: [],
    id: null,
    gradeGram: null,
    materialCode: null
  });
  const intl = useIntl();
  const { updateListSettings, settings } = useListSettings(
    ListViews.SOLD_TO_LIST
  );
  const [
    valuesPaginationState,
    setValuesPaginationState
  ] = useLocalPaginationState(settings?.rowNumber);
  const resetPaginationState = () => {
    setValuesPaginationState({
      first: settings?.rowNumber,
      after: undefined,
      before: undefined
    });
  };
  const paginateValues = useLocalPaginator(setValuesPaginationState);

  const queryVariables = React.useMemo(
    () => ({
      ...valuesPaginationState,
      soldToCode: id,
      contractNo: filterVariables.id || undefined,
      filter: {
        companyIds: filterVariables.companyIds,
        gradeGram: filterVariables.gradeGram,
        materialCode: filterVariables.materialCode
      },
      sortBy: getSortQueryVariables(params)
    }),
    [params, filterVariables, settings?.rowNumber, valuesPaginationState]
  );

  const { data, loading } = useContractsQuery({
    skip:
      !id ||
      !isStaff ||
      !!(
        !filterVariables.companyIds.length &&
        !filterVariables.gradeGram &&
        !filterVariables.materialCode
      ),
    displayLoader: true,
    variables: queryVariables
  });

  const { refetch: callCreditLimit } = useGetCreditLimitQuery({
    skip: true
  });

  const soldToExists = useMemo(() => !!data?.contracts?.totalCount, [
    data?.contracts
  ]);

  const { loadNextPage, loadPreviousPage, pageInfo } = paginateValues(
    data?.contracts?.pageInfo,
    valuesPaginationState
  );

  useEffect(() => {
    updateListSettings("rowNumber", CUSTOMER_ORDER_BY);
    return () => {
      dispatchAppState({
        payload: {
          value: false
        },
        type: "setFullScreen"
      });
    };
  }, []);

  useEffect(() => {
    resetPaginationState();
  }, [settings?.rowNumber]);

  const customerContractNode = useMemo(() => {
    if (data?.contracts) {
      return mapEdgesToItems(data?.contracts);
    }
  }, [data?.contracts]);

  const noFilterInput = useMemo(
    () => filterVariables.companyIds?.[0] === "-1",
    [filterVariables]
  );

  const handleReset = () => {
    navigate(createOrderUrl);
  };
  const handleViewContract = (
    contractId: string,
    contractNo: string,
    salesOrgCode: string,
    soldToCode: string
  ) => {
    callCreditLimit({
      input: { contractNo, salesOrgCode, soldToCode }
    }).then(({ data }) => {
      if (data?.getCreditLimit?.creditBlockStatus) {
        setOpenCustomerBlock(true);
        return;
      }
      navigate(contractDetailUrl(contractId));
    });
  };

  const handleFilterByCompanyIds = (companyIds: string[]) => {
    resetPaginationState();
    setFilterVariables({
      ...filterVariables,
      companyIds: companyIds?.length ? companyIds.map(item => +item) : ["-1"]
    });
  };
  const handleFilterByContractId = (
    contractId: string,
    gradeGram: string,
    materialCode: string
  ) => {
    resetPaginationState();
    setFilterVariables({
      ...filterVariables,
      id: contractId,
      gradeGram,
      materialCode
    });
  };

  const handleSort = field => {
    resetPaginationState();
    navigate(
      contractUrl(id, {
        ...params,
        ...getSortUrlVariables(field, params)
      }),
      { replace: true }
    );
  };

  useEffect(() => {
    dispatchAppState({
      payload: {
        customerId: id
      },
      type: "setCustomerId"
    });
  }, []);

  useEffect(() => {
    resetPaginationState();
  }, [settings?.rowNumber, filterVariables]);

  const lastPaginationState = () => {
    setValuesPaginationState({
      last: data?.contracts?.latestPageItemNumber
    });
  };

  const handleNextLastPage = () => {
    lastPaginationState();
  };

  const handlePreviousFirstPage = () => {
    resetPaginationState();
  };

  return (
    <>
      <Container>
        <Box className={classes.headerSoldToList}>
          <PageHeader
            title={intl.formatMessage({ defaultMessage: "ค้นหาลูกค้า" })}
          />
          <FullScreenButton />
        </Box>

        <SoldToPage
          onchange={() => null}
          onReset={handleReset}
          searchId={id}
          customerContracts={customerContractNode}
          soldToExists={soldToExists}
          onFilterByCompanyIds={handleFilterByCompanyIds}
          onFilterByContractId={handleFilterByContractId}
          loading={loading}
        >
          <SoldToList
            settings={settings}
            disabled={loading}
            customerContracts={customerContractNode}
            soldToExists={true}
            noFilterInput={noFilterInput}
            pageInfo={pageInfo}
            onNextPage={loadNextPage}
            onPreviousPage={loadPreviousPage}
            onUpdateListSettings={updateListSettings}
            onRowClick={() => null}
            onPreviousFirstPage={handlePreviousFirstPage}
            onNextLastPage={handleNextLastPage}
            onViewContract={handleViewContract}
            onSort={handleSort}
            sort={getSortParams(params)}
            totalCount={data?.contracts?.totalCount}
            latestPageItemNumber={data?.contracts?.latestPageItemNumber}
          />
        </SoldToPage>
      </Container>
      <CustomerBlockDialog
        content="ไม่สามารถทำรายการได้ เนื่องจากลูกค้าติด (Credit Block) กรุณาติดต่อเจ้าหน้าที่สินเชื่อ"
        open={onOpenCustomerBlock}
        onClose={() => {
          setOpenCustomerBlock(false);
        }}
      />
    </>
  );
};

export default ContractList;
